import React, { useContext, useEffect, useState } from 'react'
import './index.scss'
import SelectComponent from '@App/kit/components/Selectors/Select'
import { SelectOption } from '@Components/Selectors/Select'
import PointPreloader from '@App/kit/components/Preloaders/PointPreloader'
import { StoresContext } from '@App/contexts'
import { observer } from 'mobx-react'
import ContentHeader from '@Components/ContentHeader'
import { analyticsSendPage } from '@App/common/utils/analytics-utils'
import { RateSheetUsagePeriod } from '@Entities/statements/rate-sheet-usage-period'
import { useErrorBoundary } from 'react-error-boundary'
import RatesTable from '@Components/Tables/RoyaltiesTable/RatesTable'
import ProvisionalRateSheetFooter from '@Scenes/RightsholderScene/RightsholderRoyaltiesRateSheets/ProvisionalRateSheetFooter'

const RightsholderRoyaltiesRateSheets = () => {
    const { statementsStore } = useContext(StoresContext)

    const [loading, setLoading] = useState(true)
    const [selectedUsagePeriod, selectUsagePeriod] = useState<{
        option: SelectOption
        sheetMonth: RateSheetUsagePeriod
    }>()
    const [selectedDsp, selectDSP] = useState<SelectOption>()
    const { showBoundary } = useErrorBoundary()

    const onSelectedUsagePeriod = (value: SelectOption) => {
        const foundSheetMonth = statementsStore.ratePeriods?.find(
            (rateSheetMonth) => rateSheetMonth.period === value.value
        )
        if (foundSheetMonth) {
            selectUsagePeriod({ option: value, sheetMonth: foundSheetMonth })
        } else {
            showBoundary(new Error('Unable to select sheet month'))
        }
    }

    useEffect(() => {
        setLoading(true)
        analyticsSendPage('rightsholder_royalties_rate_sheets')
        Promise.all([
            statementsStore.ratePeriods ? Promise.resolve() : statementsStore.loadRateSheetPeriods(),
            statementsStore.rateDsps ? Promise.resolve() : statementsStore.loadRateSheetDsps(),
        ])
            .then(() => {
                onSelectedUsagePeriod(statementsStore.rateSheetUsagePeriodOptions[0])
                selectDSP(statementsStore.rateSheetDspOptions[0])
            })
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => {
        if (selectedUsagePeriod && selectedDsp) {
            statementsStore.clearDspRates()
            setLoading(true)
            statementsStore
                .loadDspRates(
                    selectedUsagePeriod.sheetMonth.year.toString(),
                    selectedUsagePeriod.sheetMonth.month.toString(),
                    selectedDsp.value !== 'All' ? selectedDsp.value : undefined
                )
                .finally(() => setLoading(false))
        }
    }, [selectedUsagePeriod, selectedDsp])

    return (
        <>
            <title>Royalty Statements</title>
            <div className="rate-sheets-wrapper">
                <ContentHeader
                    title="DSP Rate Sheets"
                    descriptionElement={
                        <>
                            <p>
                                Rate sheets illustrate how a digital service provider (DSP) calculates the rate per
                                unit. The rate per unit is the mechanical royalty rate that is applied to each stream
                                based on subscription type and usage period.
                            </p>
                            <p>Rate sheets are not unique to your account.</p>
                            <p>
                                Select the DSP and usage period from the drop-down menus below to view those rate
                                sheets.
                            </p>
                        </>
                    }
                />
                <div className="filter-block">
                    <div className="filter-item-wrapper">
                        <div className="filter-item" data-testid="dsp-options-div">
                            <p className="small bold">
                                <label htmlFor="dsp-options">DSP:</label>
                            </p>
                            <SelectComponent
                                name="dsp-options"
                                options={statementsStore.rateSheetDspOptions}
                                isDisabled={loading}
                                defaultValue={statementsStore.rateSheetDspOptions[0]}
                                selectedValue={selectedDsp}
                                onValueChange={(value: SelectOption) => selectDSP(value)}
                            />
                        </div>
                    </div>
                    <div className="filter-item-wrapper">
                        <div className="filter-item" data-testid="usage-options-div">
                            <p className="small bold">
                                <label htmlFor="usage-period-options">Usage Period:</label>
                            </p>
                            <SelectComponent
                                name="usage-period-options"
                                isDisabled={loading}
                                options={statementsStore.rateSheetUsagePeriodOptions}
                                defaultValue={statementsStore.rateSheetUsagePeriodOptions[0]}
                                selectedValue={selectedUsagePeriod?.option}
                                onValueChange={onSelectedUsagePeriod}
                            />
                        </div>
                    </div>
                </div>
                {loading ? (
                    <PointPreloader />
                ) : (
                    <>
                        <RatesTable
                            data={statementsStore.dspRates ?? []}
                            isProvisional={selectedUsagePeriod?.sheetMonth.provisional ?? false}
                        />
                        {selectedUsagePeriod?.sheetMonth.provisional && <ProvisionalRateSheetFooter />}
                    </>
                )}
            </div>
        </>
    )
}

export default observer(RightsholderRoyaltiesRateSheets)
